import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// SEMPRE URL relativi - NESSUNA dipendenza esterna
const API_URL = '';

// Timeout di inattività in millisecondi (15 minuti)
const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Usa sessionStorage - si cancella quando chiudi browser/tab o fai refresh
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Funzione logout
  const logout = useCallback(() => {
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  // Controlla se la sessione è scaduta per inattività
  const checkInactivity = useCallback(() => {
    const elapsed = Date.now() - lastActivity;
    if (elapsed > INACTIVITY_TIMEOUT && token) {
      console.log('Sessione scaduta per inattività');
      logout();
      return true;
    }
    return false;
  }, [lastActivity, token, logout]);

  // Inizializzazione autenticazione
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setLastActivity(Date.now());
        } catch (error) {
          console.error('Auth init error:', error);
          logout();
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token, logout]);

  // Listener per eventi di attività utente
  useEffect(() => {
    if (!token) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      setLastActivity(Date.now());
    };

    // Aggiungi listener per tutti gli eventi di attività
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Timer per controllo periodico inattività (ogni minuto)
    const inactivityCheckInterval = setInterval(() => {
      checkInactivity();
    }, 60000);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(inactivityCheckInterval);
    };
  }, [token, checkInactivity]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    sessionStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setLastActivity(Date.now());
    return userData;
  };

  const register = async (email, password, fullName, role = 'user') => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      full_name: fullName,
      role
    });
    const { token: newToken, user: userData } = response.data;
    sessionStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setLastActivity(Date.now());
    return userData;
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // Refresh user data from server (used to pick up admin-side changes
  // like visible_sections without forcing a hard refresh)
  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      // If token is invalid, log out
      if (error.response?.status === 401) {
        logout();
      }
      return null;
    }
  }, [token, logout]);

  // Periodically re-sync the current user so that side-effects of admin
  // changes (visible_sections, allowed_pdv_ids, is_active, role) propagate
  // to the active session within ~30 seconds.
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      refreshUser();
    }, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [token, refreshUser]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
